import { useCallback, useEffect, useState } from 'react';
import Drawer from '../common/Drawer';
import api from '../../services/api';
import { apiErrorMessage } from '../../services/errorMessage';
import type { TrendTag } from '../../types';

/**
 * The whole vocabulary, and the way back from every decision made about it.
 *
 * WHY THIS EXISTS. Dismissing a suggestion used to be a one-way door: the row
 * survived in the database as DISMISSED, but nothing listed dismissed tags, so
 * a misclick removed a bucket from the product permanently and silently. That is
 * the wrong shape for a control whose job is to stop something NAGGING — "no
 * thanks" and "never again, and you may not change your mind" are different
 * decisions and only one of them was on offer.
 *
 * Three groups, three moves between them, every one reversible:
 *
 *   In use     — offered to the model, filterable on the page
 *   Asked for  — the model wanted these; promote or dismiss
 *   Dismissed  — turned down; restorable, which is the point
 *
 * Retiring an active tag sends it to "Asked for", not to "Dismissed". Those
 * differ: one is "not right now", the other is "stop asking". A vocabulary you
 * are still shaping needs both, and collapsing them would make every retirement
 * feel permanent.
 */

const GROUPS: { status: string; title: string; blurb: string }[] = [
  {
    status: 'ACTIVE',
    title: 'In use',
    blurb: 'Offered to the tagger and filterable on the Trends list.',
  },
  {
    status: 'SUGGESTED',
    title: 'Asked for',
    blurb: 'The tagger wanted these and does not have them.',
  },
  {
    status: 'DISMISSED',
    title: 'Dismissed',
    blurb: 'Turned down. Restore one to have it offered again.',
  },
];

export default function ManageTagsDrawer({
  onClose,
  onChanged,
}: {
  onClose: () => void;
  onChanged: () => void;
}) {
  const [tags, setTags] = useState<TrendTag[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setTags(await api.listTrendTags());
    } catch (e) {
      setError(apiErrorMessage(e));
      setTags([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const move = async (tag: TrendTag, action: 'promote' | 'restore' | 'dismiss') => {
    setBusy(tag.id);
    setError(null);
    try {
      if (action === 'promote') await api.promoteTrendTag(tag.id);
      else if (action === 'restore') await api.restoreTrendTag(tag.id);
      else await api.dismissTrendTag(tag.id);
      await load();
      // The Trends list behind this changes shape — chips appear or vanish —
      // so it has to re-read rather than wait for the next poll.
      onChanged();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const inGroup = (status: string) => (tags ?? []).filter(t => t.status === status);

  return (
    <Drawer onClose={onClose} side="right" width="w-[34rem]" eyebrow="Trends" title="Story tags">
      <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
        The buckets a trending story can be filed under. The tagger may only use
        what is <span className="font-medium">in use</span>, which is what keeps
        the list from sprawling - and it tells you what it wished it had.
      </p>

      {error && (
        <div className="mb-3 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
          {error}
        </div>
      )}

      {tags === null && <p className="text-sm text-gray-500">Loading…</p>}

      {tags !== null &&
        GROUPS.map(group => {
          const rows = inGroup(group.status);
          return (
            <section key={group.status} className="mb-5">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {group.title}{' '}
                <span className="font-normal text-gray-400">{rows.length}</span>
              </h3>
              <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">{group.blurb}</p>

              {rows.length === 0 ? (
                <p className="text-xs italic text-gray-400 dark:text-gray-500">
                  {group.status === 'DISMISSED'
                    ? 'Nothing dismissed.'
                    : group.status === 'SUGGESTED'
                      ? 'Nothing outstanding - the list is covering what comes up.'
                      : 'No tags in use.'}
                </p>
              ) : (
                <ul className="divide-y divide-gray-100 rounded border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
                  {rows.map(tag => (
                    <li
                      key={tag.id}
                      className="flex items-start justify-between gap-3 px-2.5 py-2"
                    >
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
                          {tag.label}
                        </span>
                        <span className="ml-1.5 text-xs text-gray-400">{tag.value}</span>
                        {tag.hint && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">{tag.hint}</p>
                        )}
                        {tag.times_suggested > 0 && (
                          <p className="text-xs text-gray-400 dark:text-gray-500">
                            Asked for{' '}
                            {tag.times_suggested === 1
                              ? 'once'
                              : `${tag.times_suggested} times`}
                          </p>
                        )}
                      </div>

                      <div className="flex shrink-0 items-center gap-2 text-xs">
                        {group.status !== 'ACTIVE' && (
                          <button
                            onClick={() => move(tag, 'promote')}
                            disabled={busy === tag.id}
                            className="rounded bg-blue-600 px-2 py-0.5 font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                          >
                            Use it
                          </button>
                        )}
                        {group.status === 'ACTIVE' && (
                          <button
                            onClick={() => move(tag, 'restore')}
                            disabled={busy === tag.id}
                            className="text-gray-500 underline hover:text-gray-700 disabled:opacity-50 dark:text-gray-400 dark:hover:text-gray-200"
                            title="Stop offering it, without declaring it unwanted"
                          >
                            Retire
                          </button>
                        )}
                        {group.status === 'SUGGESTED' && (
                          <button
                            onClick={() => move(tag, 'dismiss')}
                            disabled={busy === tag.id}
                            className="text-gray-500 underline hover:text-gray-700 disabled:opacity-50 dark:text-gray-400 dark:hover:text-gray-200"
                          >
                            Dismiss
                          </button>
                        )}
                        {group.status === 'DISMISSED' && (
                          <button
                            onClick={() => move(tag, 'restore')}
                            disabled={busy === tag.id}
                            className="text-gray-500 underline hover:text-gray-700 disabled:opacity-50 dark:text-gray-400 dark:hover:text-gray-200"
                          >
                            Un-dismiss
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
    </Drawer>
  );
}
